import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, Form, Input, Button, message, Tabs, Table, Tag, Popconfirm, Spin } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import KvkkContentModal from '../components/common/KvkkContentModal';
import NotificationsHistoryTab from '../components/common/NotificationsHistoryTab';
import MyLeavesTab from '../components/common/MyLeavesTab';
import { changePassword } from '../api/auth';
import useAuthStore from '../store/authStore';
import api from '../api/axios';
import DelegationsTab from '../components/settings/DelegationsTab';
import { getMyConsents, getMyKvkkStatus, acceptKvkk } from '../api/kvkk';
import dayjs from 'dayjs';
import PhoneInput from '../components/common/PhoneInput';
import { apiErrorMessage } from '../utils/apiError';

const { TabPane } = Tabs;

const DELEGATION_ROLES = ['developer_admin', 'super_admin', 'branch_manager', 'teacher'];

const STATUS_TAGS = {
  active: <Tag color="green">Onaylandı</Tag>,
  rejected: <Tag color="red">Reddedildi</Tag>,
  pending: <Tag color="gold">Bekliyor</Tag>,
};

const MyOptionalConsentsCard = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [decidingKey, setDecidingKey] = useState(null);
  const [modalData, setModalData] = useState(null);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getMyKvkkStatus();
      setItems(res.data.data || []);
    } catch {
      message.error('Onay metinleri yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleDecide = async (type, decision) => {
    setDecidingKey(type);
    try {
      await acceptKvkk(type, decision);
      message.success(decision === 'accept' ? 'Onaylandı.' : 'Reddedildi.');
      fetchStatus();
    } catch (error) {
      message.error(apiErrorMessage(error, 'İşlem başarısız.'));
    } finally {
      setDecidingKey(null);
    }
  };

  if (loading) return <div className="flex justify-center py-8"><Spin /></div>;
  if (items.length === 0) return null;

  return (
    <>
      <Card title="Onay Metinlerim" className="mb-4">
        <div className="space-y-2">
          {items.map(item => (
            <div key={item.type} className="flex items-center justify-between flex-wrap gap-2 py-2 border-t first:border-t-0">
              <div className="flex items-center gap-2">
                <span>{item.label}</span>
                {STATUS_TAGS[item.status]}
                <Button size="small" onClick={() => setModalData(item)}>Metni Görüntüle</Button>
              </div>
              <div className="flex gap-2">
                {item.status !== 'active' && (
                  <Button
                    size="small"
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    loading={decidingKey === item.type}
                    onClick={() => handleDecide(item.type, 'accept')}
                  >
                    Onayla
                  </Button>
                )}
                {!item.is_mandatory && item.status !== 'rejected' && (
                  <Popconfirm
                    title="Reddetmek istediğinize emin misiniz?"
                    onConfirm={() => handleDecide(item.type, 'reject')}
                    okText="Evet, reddet"
                    cancelText="Vazgeç"
                  >
                    <Button size="small" danger icon={<CloseCircleOutlined />} loading={decidingKey === item.type}>
                      Reddet
                    </Button>
                  </Popconfirm>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>
      <KvkkContentModal
        open={!!modalData}
        onClose={() => setModalData(null)}
        content={modalData?.text?.content}
        version={modalData?.text?.version}
        label={modalData?.label}
      />
    </>
  );
};

const KvkkConsentsTab = () => {
  const [consents, setConsents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalData, setModalData] = useState(null);

  useEffect(() => {
    getMyConsents()
      .then(res => setConsents(res.data.data))
      .catch(() => message.error('Onaylar yüklenemedi.'))
      .finally(() => setLoading(false));
  }, []);

  const columns = [
    { title: 'Tür', render: (_, r) => r.kvkk_text?.text_type?.label || r.kvkk_text?.type },
    { title: 'Versiyon', render: (_, r) => `v${r.kvkk_text?.version}` },
    { title: 'Onay Tarihi', render: (_, r) => dayjs(r.accepted_at).format('DD.MM.YYYY HH:mm') },
    {
      title: 'Durum',
      render: (_, r) => r.status === 'active'
        ? <Tag color="green">Aktif</Tag>
        : <Tag color="red">İptal edildi</Tag>,
    },
    {
      title: 'Onaylanan Metin',
      render: (_, r) => (
        <Button size="small" onClick={() => setModalData(r.kvkk_text)}>
          Görüntüle
        </Button>
      ),
    },
  ];

  return (
    <>
      <MyOptionalConsentsCard />
      <Card title="Onay Geçmişim">
        <Table rowKey="id" columns={columns} dataSource={consents} loading={loading} pagination={false} />
      </Card>
      <KvkkContentModal
        open={!!modalData}
        onClose={() => setModalData(null)}
        content={modalData?.content}
        version={modalData?.version}
        type={modalData?.type}
      />
    </>
  );
};

const Profile = () => {
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get('tab') || 'info';
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const { user, updateUser } = useAuthStore();
  const canDelegate = DELEGATION_ROLES.includes(user?.role);
  const [passwordForm] = Form.useForm();
  const [profileForm] = Form.useForm();

  const handlePasswordChange = async (values) => {
    setPasswordLoading(true);
    try {
      await changePassword(values.current_password, values.password, values.password_confirmation);
      message.success('Şifre başarıyla güncellendi.');
      passwordForm.resetFields();
    } catch (error) {
      message.error(apiErrorMessage(error, 'Şifre güncellenemedi.'));
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleProfileUpdate = async (values) => {
    setProfileLoading(true);
    try {
      await api.put(`/users/${user.id}`, values);
      // DIKKAT: PUT /users/{id} ham User modelini donuyor - icinde
      // `permissions` YOK. Eskiden bu yanit dogrudan updateUser'a veriliyordu
      // ve localStorage'daki kullanici izinsiz halle EZILIYORDU. Sonuc:
      // profilini kaydeden herkes o oturum boyunca izne bagli her seyi
      // kaybediyordu - Okullar'daki "Ekle" butonu, Odemeler/Raporlar/
      // Kullanicilar menu kalemleri... Tam sayfa yenilemede /auth/me tekrar
      // doldurdugu icin sorun kendiliginden gecip duruyor, bu yuzden
      // teshisi zordu. Bu yuzden kaydettikten sonra kullaniciyi /auth/me'den
      // TAZE cekiyoruz; tek dogru kaynak orasi.
      const me = await api.get('/auth/me');
      updateUser(me.data);
      message.success('Profil güncellendi.');
    } catch (error) {
      message.error(apiErrorMessage(error, 'Profil güncellenemedi.'));
    } finally {
      setProfileLoading(false);
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">Profil</h2>

      <Tabs defaultActiveKey={defaultTab}>
        <TabPane tab="Profil Bilgileri" key="info">
          <Card>
            <Form
              form={profileForm}
              layout="vertical"
              initialValues={{
                name: user?.name,
                surname: user?.surname,
                phone: user?.phone,
                email: user?.email,
              }}
              onFinish={handleProfileUpdate}
              style={{ maxWidth: 500 }}
            >
              <div className="grid grid-cols-2 gap-4">
                <Form.Item name="name" label="Ad" rules={[{ required: true }]}>
                  <Input />
                </Form.Item>
                <Form.Item name="surname" label="Soyad" rules={[{ required: true }]}>
                  <Input />
                </Form.Item>
              </div>
              <Form.Item
                name="email"
                label="E-posta"
                rules={[
                  { required: true, message: 'E-posta giriniz.' },
                  { type: 'email', message: 'Geçerli e-posta giriniz.' },
                ]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="phone"
                label="Telefon"
                rules={[{ pattern: /^\+\d{8,15}$/, message: 'Ülke kodunu seçip numarayı giriniz.' }]}
              >
                <PhoneInput />
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit" loading={profileLoading}>
                  Güncelle
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </TabPane>

        {canDelegate && (
          <TabPane tab="Vekaletlerim" key="delegations">
            <DelegationsTab mine />
          </TabPane>
        )}

        <TabPane tab="Bildirimlerim" key="notifications">
          <NotificationsHistoryTab />
        </TabPane>

        <TabPane tab="Onaylarım" key="kvkk">
          <KvkkConsentsTab />
        </TabPane>

        <TabPane tab="İzinlerim" key="leaves">
          <MyLeavesTab />
        </TabPane>

        <TabPane tab="Şifre Değiştir" key="password">
          <Card>
            <Form
              form={passwordForm}
              layout="vertical"
              onFinish={handlePasswordChange}
              style={{ maxWidth: 400 }}
            >
              <Form.Item
                name="current_password"
                label="Mevcut Şifre"
                rules={[{ required: true, message: 'Mevcut şifrenizi giriniz.' }]}
              >
                <Input.Password />
              </Form.Item>
              <Form.Item
                name="password"
                label="Yeni Şifre"
                rules={[
                  { required: true, message: 'Yeni şifrenizi giriniz.' },
                  { min: 8, message: 'Şifre en az 8 karakter olmalıdır.' },
                ]}
              >
                <Input.Password />
              </Form.Item>
              <Form.Item
                name="password_confirmation"
                label="Yeni Şifre Tekrar"
                dependencies={['password']}
                rules={[
                  { required: true, message: 'Şifrenizi tekrar giriniz.' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Şifreler eşleşmiyor.'));
                    },
                  }),
                ]}
              >
                <Input.Password />
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit" loading={passwordLoading}>
                  Şifreyi Güncelle
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </TabPane>
      </Tabs>
    </div>
  );
};

export default Profile;